import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';

import ActionsPauseNormalXsmall from '@strava/icons/ActionsPauseNormalXsmall';
import ActionsPlayNormalXsmall from '@strava/icons/ActionsPlayNormalXsmall';
import ActionsResetNormalXsmall from '@strava/icons/ActionsResetNormalXsmall';
import { isMobile } from '@strava/utils/browser';

import { useFlyoverState } from '../../hooks/useFlyoverState';
import { useMRE } from '../../hooks/useMRE';

import styles from './FlyoverSpeedControl.module.scss';

const POSSIBLE_SPEEDS = [1, 2, 3, 5, 10];

export const FlyoverSpeedControl = () => {
  const { t } = useMRE();
  const {
    playbackState,
    setPlaybackSpeedMultiplier,
    pause,
    resume,
    restart,
    progress,
    setProgress,
    userAgent
  } = useFlyoverState();

  const [currentSpeedIndex, setCurrentSpeedIndex] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [multiplierText, setMultiplierText] = useState('1x');
  const [isVisible, setIsVisible] = useState(true);

  const scrubberRef = React.useRef<HTMLDivElement>(null);
  const hideTimerHandle = React.useRef<number | undefined>(undefined);

  const setHideTimeout = useCallback(
    (delay = 2000) => {
      window.clearTimeout(hideTimerHandle.current);
      hideTimerHandle.current = window.setTimeout(() => {
        if (playbackState === 'playing') {
          // we only hide the scrubber when we are playing
          setIsVisible(false);
        }
      }, delay);
    },
    [playbackState]
  );

  const onMapContainerMouseMove = useCallback(() => {
    if (isMobile(userAgent)) {
      return;
    }

    setIsVisible(true);
    setHideTimeout();
  }, [setHideTimeout, userAgent]);

  const onKeydown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        setIsVisible(true);
        setHideTimeout();
      }
    },
    [setHideTimeout]
  );

  const onTouchEnd = useCallback(() => {
    if (isVisible) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
    setHideTimeout();
  }, [setHideTimeout, isVisible]);

  const onEnterControlContainer = (event: MouseEvent | TouchEvent) => {
    event.stopPropagation(); // Required to prevent `onTouchEnd` from firing on touch inside control container
    // To prevent the control from being hidden when the mouse is inside it.
    clearHideTimeout();
  };

  const clearHideTimeout = () => {
    window.clearTimeout(hideTimerHandle.current);
  };

  // Initial setup - onAttach
  useEffect(() => {
    setHideTimeout();
    // we want this to execute once onAttach
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (playbackState === 'playing') {
      setHideTimeout();
    }
  }, [playbackState, setHideTimeout]);

  // Make control visible when flyover finished
  useEffect(() => {
    if (playbackState === 'finished') {
      setIsVisible(true);
    }
  }, [playbackState]);

  useEffect(() => {
    document.addEventListener('mousemove', onMapContainerMouseMove);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMapContainerMouseMove);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onMapContainerMouseMove, onKeydown, onTouchEnd]);

  const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => {
    return (e as TouchEvent).touches !== undefined;
  };

  const updateProgressFromScrubPosition = (e: MouseEvent | TouchEvent) => {
    const scrubber = scrubberRef.current as HTMLDivElement;
    const bounds = scrubber.getBoundingClientRect();
    const x = isTouchEvent(e) ? e.touches[0]?.clientX : e.clientX;
    if (x) {
      const progress = (x - bounds.left) / bounds.width;
      onProgressUpdated(progress);
    }
  };

  enum CycleMode {
    FORWARD,
    BACKWARD
  }

  const onCycleSpeeds = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    mode: CycleMode
  ) => {
    e.preventDefault();
    let newSpeedIndex = 0;
    if (mode === CycleMode.FORWARD) {
      newSpeedIndex =
        currentSpeedIndex >= POSSIBLE_SPEEDS.length - 1
          ? 0
          : currentSpeedIndex + 1;
    } else {
      newSpeedIndex =
        currentSpeedIndex <= 0
          ? POSSIBLE_SPEEDS.length - 1
          : currentSpeedIndex - 1;
    }
    const multiplier = POSSIBLE_SPEEDS[newSpeedIndex] as number;
    setCurrentSpeedIndex(newSpeedIndex);
    setMultiplierText(`${multiplier}x`);
    setPlaybackSpeedMultiplier(multiplier);
  };

  const onScrubStart = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation(); // stop propagation up to listeners on map container
    setIsScrubbing(true);
    clearHideTimeout();

    document.addEventListener('mouseup', (e) => onScrubEnd(e as MouseEvent));
    document.addEventListener('touchend', onScrubEnd);
    updateProgressFromScrubPosition(e);
  };

  const onScrubEnd = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation(); // stop propagation up to listeners on map container
    setIsScrubbing(false);
    document.removeEventListener('mouseup', onScrubEnd);
    document.removeEventListener('touchend', onScrubEnd);

    if (isTouchEvent(e)) {
      // For touch events to trigger the control hide timeout.
      // Hacky use of setTimeout to make sure that this step (only for touch events)
      // executes after and therefore takes priority over _onMouseEnterControlContainer (both mouse and touch)
      setTimeout(() => {
        setHideTimeout();
      }, 0);
    }
  };

  const onScrubMove = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation(); // stop propagation up to listeners on map container
    if (isScrubbing) {
      updateProgressFromScrubPosition(e);
    }
  };

  // called based on drag position
  const onProgressUpdated = (progress: number) => {
    // When progress reaches 1, the flyoverHandle promise will resolve and the flyover will exit.
    // To allow manual scrubbing to the end of the line when paused, we cap drag position progress to
    // "nearly at the end" of the line.
    // TODO may not be necessary any more with new refactor
    const sanitizedProgress = Math.max(Math.min(progress, 0.999), 0);
    setProgress(sanitizedProgress);
  };

  const button = useMemo(() => {
    if (playbackState === 'finished') {
      return (
        <button
          className={styles.flyoverSpeedControlBtn}
          title={t('mre.flyover.start')}
          onClick={restart}
          data-testid="mre-flyover-speed-control-restart"
        >
          <ActionsResetNormalXsmall />
        </button>
      );
    }

    if (playbackState === 'playing') {
      return (
        <button
          className={styles.flyoverSpeedControlBtn}
          title={t('mre.flyover.pause')}
          onClick={pause}
          data-testid="mre-flyover-speed-control-pause"
        >
          <ActionsPauseNormalXsmall />
        </button>
      );
    }

    return (
      <button
        data-testid="mre-flyover-speed-control-play"
        className={styles.flyoverSpeedControlBtn}
        title={t('mre.flyover.resume')}
        onClick={resume}
      >
        <ActionsPlayNormalXsmall />
      </button>
    );
  }, [playbackState, restart, pause, resume, t]);

  return (
    /* Note: If the DOM structure changes here, this selector may need to be
    updated - https://github.com/strava/web/blob/main/apps/core/pages/activities/%5Bid%5D/flyover.module.scss#L22 */
    <div
      className={clsx(
        styles.flyoverSpeedControl,
        'flyover-speed-control-global', // for global override in flyover.module.scss
        !isVisible && styles.fadeOut
      )}
      onMouseEnter={(e) => onEnterControlContainer(e.nativeEvent)}
      onTouchEnd={(e) => onEnterControlContainer(e.nativeEvent)}
      data-testid="mre-flyover-speed-control"
    >
      {button}
      <div
        className={styles.flyoverSpeedControlScrubber}
        ref={scrubberRef}
        onMouseDown={(e) => onScrubStart(e.nativeEvent)}
        onMouseMove={(e) => onScrubMove(e.nativeEvent)}
        onTouchStart={(e) => onScrubStart(e.nativeEvent)}
        onTouchMove={(e) => onScrubMove(e.nativeEvent)}
        data-testid="mre-flyover-speed-control-scrubber"
      >
        <div className={styles.lineFull} />
        <div
          className={styles.lineBehind}
          style={{ width: `${progress * 100}%` }}
          data-testid="mre-flyover-speed-control-line-behind"
        />
        <div
          className={styles.handle}
          style={{ left: `${progress * 100}%` }}
          data-testid="mre-flyover-speed-control-handle"
        >
          <div className={styles.handleInner} />
        </div>
      </div>
      <button
        className={styles.flyoverSpeedControlBtn}
        title={t('mre.flyover.change_speed')}
        onClick={(e) => onCycleSpeeds(e, CycleMode.FORWARD)}
        onContextMenu={(e) => onCycleSpeeds(e, CycleMode.BACKWARD)}
        data-testid="mre-flyover-speed-control-speed"
      >
        {multiplierText}
      </button>
    </div>
  );
};
